<template>
  <div class="criipto-method-container">
    <FriedMessage
      v-if="isFailedSignAttempt"
      class="error-message"
      :type="MessageType.Error"
      :title="t('cr_bankid_unknown_error')"
    >
      {{ t('cr_error_msg_try_again_or_contact_sender') }}
    </FriedMessage>
    <FriedButton
      class="sign-button"
      :data-external="dataExternalName"
      :loading="redirecting"
      @click="criiptoRedirect"
    >
      {{ t('cr_sign_using_eid', { serviceName }) }}
    </FriedButton>
  </div>
</template>
<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { computed, ref } from 'vue';
import { MessageType } from '@getaccept/fried-tofu';
import { whenever } from '@vueuse/core';
import { EidType } from '../../types/enums/eid-type';
import { useResumeEidSigning } from '../composables/resume-eid-signing.composable';
import { EidHelper } from '../../helpers/eid.helper';
import { useRootStore } from '../../../store/root.store';
import { CriiptoStatus } from '../../types/enums/criipto-status';

interface Props {
  eidType: EidType.BankIDFI | EidType.NO | EidType.MitID;
}

const props = defineProps<Props>();
const emit = defineEmits(['sign']);

const { t } = useI18n();
const {
  eidType: resumedEidType,
  criiptoMethodSigningStatus,
  resetQueryParams,
} = useResumeEidSigning();

const rootStore = useRootStore();

const serviceName = computed(() => {
  switch (props.eidType) {
    case EidType.BankIDFI:
      return t('cr_finnish_trust_network');
    case EidType.NO:
      return t('cr_bankid_norge');
    case EidType.MitID:
      return t('cr_mitid');
    default:
      return '';
  }
});

const dataExternalName = computed(() => {
  switch (props.eidType) {
    case EidType.BankIDFI:
      return 'eid-signing-bankid-fi-sign-button';
    case EidType.NO:
      return 'eid-signing-bankid-no-sign-button';
    case EidType.MitID:
      return 'eid-signing-mitid-sign-button';
    default:
      return '';
  }
});

const isResumedSignAttempt = computed(() => resumedEidType.value === props.eidType);

const isSuccessfulSignAttempt = computed(
  () => isResumedSignAttempt.value && criiptoMethodSigningStatus.value === CriiptoStatus.Success
);

const isFailedSignAttempt = computed(
  () => isResumedSignAttempt.value && criiptoMethodSigningStatus.value === CriiptoStatus.Error
);

whenever(isSuccessfulSignAttempt, () => emit('sign'), { immediate: true });

const criiptoRedirect = () => {
  resetQueryParams();
  redirecting.value = true;

  EidHelper.criiptoRedirect(
    props.eidType,
    rootStore.documentId,
    rootStore.recipientId,
    rootStore.authId
  );
};

const redirecting = ref(false);
</script>
<style lang="scss" scoped>
.criipto-method-container {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-100);
}
</style>
